import { useMutation } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import { DraftNotice } from '@/components/DraftNotice';
import { SearchSelect, type SearchOption } from '@/components/SearchSelect';
import { ErrorNote, Field, FormActions, FormGroup, FormPanel, Select, TextArea, errorMessage, todayIso } from '@/components/ui';
import { useDraft } from '@/stores/drafts';
import {
  FREQUENCY_TEXT,
  PAY_METHOD_TEXT,
  WEEKDAYS,
  WORKER_TYPE_TEXT,
  employeeLabel,
  taxYearLabel,
  taxYearOf,
  useEmployeeOptions,
  useFarmOptions,
  useHrLookups,
  useShedOptions,
  type EmployeeDetail,
} from '@/hooks/hr';
import { useAuth } from '@/stores/auth';
import { PayLinesEditor } from './EmployeePay';
import {
  CNIC_RE,
  IBAN_RE,
  WALLET_RE,
  cleanIban,
  formatCnic,
  isMoney,
  payInput,
  payProblem,
  structureComponents,
  type PayAmounts,
} from './employeeRules';

const TEXT_KEYS = [
  'nameEn',
  'nameUr',
  'fatherName',
  'cnic',
  'cnicExpiry',
  'dateOfBirth',
  'gender',
  'contactNo',
  'otherContact',
  'homeAddress',
  'emergencyName',
  'emergencyPhone',
  'departmentId',
  'designationId',
  'shiftId',
  'reportsToId',
  'userId',
  'probationEnd',
  'payFrequency',
  'paymentMethod',
  'payBankName',
  'payAccountTitle',
  'payIban',
  'walletProvider',
  'walletNo',
  'eobiNo',
  'pessiNo',
  'ntn',
  'openingTaxableYtd',
  'openingTaxYtd',
  'openingTaxYear',
] as const;
type TextKey = (typeof TEXT_KEYS)[number];

const FLAG_KEYS = ['overtimeEligible', 'eobiRegistered', 'pessiRegistered', 'taxApplies'] as const;
type FlagKey = (typeof FLAG_KEYS)[number];

type Values = Record<TextKey, string> & Record<FlagKey, boolean> & { weeklyOff: number[] };

/** Fields the server leaves out for someone without the personal-details or
 *  pay permission. On edit they show as hidden and are never sent back. */
const MASKABLE: TextKey[] = [
  'cnic',
  'cnicExpiry',
  'dateOfBirth',
  'homeAddress',
  'emergencyName',
  'emergencyPhone',
  'payBankName',
  'payAccountTitle',
  'payIban',
  'walletNo',
  'eobiNo',
  'pessiNo',
  'openingTaxableYtd',
  'openingTaxYtd',
];

function initialValues(e: EmployeeDetail | undefined): Values {
  const s = (v: string | number | null | undefined) => (v === null || v === undefined ? '' : String(v));
  const amount = (v: string | null | undefined) => (v ? String(Number(v)) : '');
  return {
    nameEn: s(e?.nameEn),
    nameUr: s(e?.nameUr),
    fatherName: s(e?.fatherName),
    cnic: s(e?.cnic),
    cnicExpiry: s(e?.cnicExpiry),
    dateOfBirth: s(e?.dateOfBirth),
    gender: s(e?.gender),
    contactNo: s(e?.contactNo),
    otherContact: s(e?.otherContact),
    homeAddress: s(e?.homeAddress),
    emergencyName: s(e?.emergencyName),
    emergencyPhone: s(e?.emergencyPhone),
    departmentId: s(e?.departmentId),
    designationId: s(e?.designationId),
    shiftId: s(e?.shiftId),
    reportsToId: s(e?.reportsToId),
    userId: s(e?.userId),
    probationEnd: s(e?.probationEnd),
    payFrequency: e?.payFrequency ?? 'MONTHLY',
    paymentMethod: e?.paymentMethod ?? 'CASH',
    payBankName: s(e?.payBankName),
    payAccountTitle: s(e?.payAccountTitle),
    payIban: s(e?.payIban),
    walletProvider: s(e?.walletProvider),
    walletNo: s(e?.walletNo),
    eobiNo: s(e?.eobiNo),
    pessiNo: s(e?.pessiNo),
    ntn: s(e?.ntn),
    openingTaxableYtd: amount(e?.openingTaxableYtd),
    openingTaxYtd: amount(e?.openingTaxYtd),
    openingTaxYear: e?.openingTaxYear ? String(e.openingTaxYear) : String(taxYearOf(todayIso())),
    overtimeEligible: e?.overtimeEligible ?? false,
    eobiRegistered: e?.eobiRegistered ?? false,
    pessiRegistered: e?.pessiRegistered ?? false,
    taxApplies: e?.taxApplies ?? false,
    weeklyOff: e?.weeklyOff ?? [],
  };
}

/** A tick box laid out like the other fields. */
export function Check({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="settings__toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled ?? false} />
      <span className="field__label">{label}</span>
    </label>
  );
}

/** Checks everything the server would, in plain words, before sending. */
function problemOf(
  v: Values,
  opts: {
    creating: boolean;
    workerType: string;
    joinedOn: string;
    farmId: string;
    allFarms: boolean;
    payCheck: () => string | null;
    hidden: (k: TextKey) => boolean;
  },
): string | null {
  if (!v.nameEn.trim()) return "Enter the person's name.";
  if (v.cnic && !opts.hidden('cnic') && !CNIC_RE.test(v.cnic)) return 'CNIC must look like 35202-1234567-1 (13 digits).';
  if (opts.creating) {
    if (!opts.workerType) return 'Choose monthly salary or daily wage.';
    if (!opts.joinedOn) return 'Enter the joining date.';
    if (!opts.farmId && !opts.allFarms) return 'Choose the farm they work on.';
  }
  if (v.dateOfBirth && v.dateOfBirth >= todayIso()) return 'Date of birth must be in the past.';
  if (v.probationEnd && opts.joinedOn && v.probationEnd < opts.joinedOn) return 'Probation cannot end before they joined.';
  if (opts.creating) {
    const p = opts.payCheck();
    if (p) return p;
  }
  if (v.payIban && !opts.hidden('payIban') && !IBAN_RE.test(cleanIban(v.payIban))) {
    return 'IBAN must be 24 characters: PK, 2 digits, 4 bank letters, then 16 letters or digits.';
  }
  if (v.paymentMethod === 'WALLET' && !v.walletProvider) return 'Choose JazzCash or Easypaisa.';
  if (v.walletNo && !opts.hidden('walletNo') && !WALLET_RE.test(v.walletNo.trim())) {
    return 'Wallet number must be 11 digits starting 03, like 03001234567.';
  }
  if (v.openingTaxableYtd && !opts.hidden('openingTaxableYtd') && !isMoney(v.openingTaxableYtd)) {
    return 'Taxable pay so far must be a number.';
  }
  if (v.openingTaxYtd && !opts.hidden('openingTaxYtd') && !isMoney(v.openingTaxYtd)) return 'Tax so far must be a number.';
  return null;
}

/** The value as the API takes it: blank text is "none". */
function wire(key: TextKey, v: Values): string | number | null {
  const raw = v[key].trim();
  if (key === 'openingTaxYear') return raw ? Number(raw) : null;
  if (key === 'payIban') return raw ? cleanIban(raw) : null;
  if (key === 'payFrequency' || key === 'paymentMethod') return raw;
  return raw || null;
}

/**
 * Add or edit a person, full-page. On edit, worker type, joining date, farm
 * and pay are shown but changed through their own actions (Change pay, Move
 * to another farm), so each change keeps its own record.
 */
export function EmployeeForm({
  editing,
  presetFarmId,
  onCancel,
  onSaved,
}: {
  editing: EmployeeDetail | undefined;
  /** '' for head office, a farm id, or null to leave it open. */
  presetFarmId: string | null;
  onCancel: () => void;
  onSaved: (message: string, id: string) => void;
}) {
  const creating = editing === undefined;
  const allFarms = useAuth((s) => s.user?.scope.allFarms ?? false);
  const lookups = useHrLookups();
  const farms = useFarmOptions();
  const people = useEmployeeOptions('CURRENT');

  const [initial] = useState(() => initialValues(editing));
  const [v, setV] = useState<Values>(initial);
  const [workerType, setWorkerType] = useState<'' | 'MONTHLY' | 'DAILY'>(editing?.workerType ?? '');
  const [joinedOn, setJoinedOn] = useState(editing?.joinedOn ?? todayIso());
  const [farmId, setFarmId] = useState(editing?.farmId ?? presetFarmId ?? '');
  const [shedId, setShedId] = useState(editing?.shedId ?? '');
  const [amounts, setAmounts] = useState<PayAmounts>({});
  const [dailyRate, setDailyRate] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const sheds = useShedOptions(farmId || null);

  // The longest form in the app: thirty-odd fields, often filled in from a
  // paper file. Losing it to a stray reload is the worst case this exists for.
  const draft = useDraft(
    editing ? `employee.edit.${editing.id}` : 'employee.new',
    { v, workerType, joinedOn, farmId, shedId, amounts, dailyRate },
    (saved) => {
      setV(saved.v);
      setWorkerType(saved.workerType);
      setJoinedOn(saved.joinedOn);
      setFarmId(saved.farmId);
      setShedId(saved.shedId);
      setAmounts(saved.amounts);
      setDailyRate(saved.dailyRate);
    },
  );

  function startAgain() {
    setV(initial);
    setWorkerType(editing?.workerType ?? '');
    setJoinedOn(editing?.joinedOn ?? todayIso());
    setFarmId(editing?.farmId ?? presetFarmId ?? '');
    setShedId(editing?.shedId ?? '');
    setAmounts({});
    setDailyRate('');
    setProblem(null);
  }

  const components = structureComponents(lookups.data?.payComponents);
  const hidden = (k: TextKey) => editing !== undefined && MASKABLE.includes(k) && !(k in editing);
  const set = (key: TextKey) => (value: string) => setV((s) => ({ ...s, [key]: value }));
  const flag = (key: FlagKey) => (value: boolean) => setV((s) => ({ ...s, [key]: value }));

  const save = useMutation({
    mutationFn: async (): Promise<{ id: string; code: string }> => {
      if (editing) {
        const body: Record<string, unknown> = {};
        for (const k of TEXT_KEYS) {
          if (hidden(k)) continue;
          if (v[k].trim() !== initial[k].trim()) body[k] = wire(k, v);
        }
        for (const k of FLAG_KEYS) if (v[k] !== initial[k]) body[k] = v[k];
        if ([...v.weeklyOff].sort().join() !== [...initial.weeklyOff].sort().join()) body.weeklyOff = v.weeklyOff;
        if (Object.keys(body).length > 0) await api.patch(`/api/v1/hr/employees/${editing.id}`, body);
        return { id: editing.id, code: editing.code };
      }
      const body: Record<string, unknown> = {
        workerType,
        joinedOn,
        farmId: farmId || null,
        shedId: farmId && shedId ? shedId : null,
        pay: payInput(workerType as 'MONTHLY' | 'DAILY', amounts, dailyRate),
        weeklyOff: v.weeklyOff,
      };
      for (const k of TEXT_KEYS) {
        const w = wire(k, v);
        if (w !== null && w !== '') body[k] = w;
      }
      for (const k of FLAG_KEYS) body[k] = v[k];
      if (workerType === 'MONTHLY') body.payFrequency = 'MONTHLY';
      if (!v.openingTaxableYtd.trim() && !v.openingTaxYtd.trim()) delete body.openingTaxYear;
      return api.post<{ id: string; code: string }>('/api/v1/hr/employees', body);
    },
    onSuccess: (r) => {
      draft.forget();
      onSaved(creating ? `${r.code} ${v.nameEn.trim()} added.` : `${r.code} ${v.nameEn.trim()} saved.`, r.id);
    },
    onError: (e) => setProblem(errorMessage(e, 'Could not save')),
  });

  function submit() {
    const p = problemOf(v, {
      creating,
      workerType,
      joinedOn,
      farmId,
      allFarms,
      hidden,
      payCheck: () => (workerType ? payProblem(workerType, components, amounts, dailyRate) : null),
    });
    if (p) return setProblem(p);
    setProblem(null);
    save.mutate();
  }

  const masked = (k: TextKey, node: ReactNode, label: string) =>
    hidden(k) ? <Field key={k} label={label} value="Hidden" onChange={() => {}} disabled hint="You cannot see or change this." /> : node;

  const farmOptions = (farms.data ?? []).map((f) => ({ value: f.id, label: `${f.code} · ${f.nameEn}` }));
  const named = (list: Array<{ id: string; code: string; nameEn: string }> | undefined) =>
    (list ?? []).map((x) => ({ value: x.id, label: x.nameEn }));
  const taxYear = taxYearOf(todayIso());
  const farmLabel = editing ? (editing.farmCode ? `${editing.farmCode} · ${editing.farmName ?? ''}` : 'Head office') : '';

  return (
    <FormPanel
      title={editing ? `Edit ${editing.code} — ${editing.nameEn}` : 'Add a person'}
      icon={<AppIcon kind={editing ? 'employee' : 'joiner'} />}
      intro={
        editing
          ? 'Pay, farm and status each have their own action (Change pay, Move to another farm, Suspend, Mark as left), so every change keeps its own record.'
          : 'Fields marked * are needed. Their first pay is recorded as approved when you save.'
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DraftNotice draft={draft} onStartAgain={startAgain} />

        <FormGroup title="Person" columns={3} card>
          <Field label="Name" required value={v.nameEn} onChange={set('nameEn')} />
          <Field label="Name in Urdu" urdu value={v.nameUr} onChange={set('nameUr')} />
          <Field label="Father's or husband's name" value={v.fatherName} onChange={set('fatherName')} />
          {masked(
            'cnic',
            <Field
              label="CNIC"
              mono
              value={v.cnic}
              onChange={(x) => set('cnic')(formatCnic(x))}
              placeholder="00000-0000000-0"
            />,
            'CNIC',
          )}
          {masked('cnicExpiry', <Field label="CNIC expiry" type="date" value={v.cnicExpiry} onChange={set('cnicExpiry')} />, 'CNIC expiry')}
          {masked('dateOfBirth', <Field label="Date of birth" type="date" value={v.dateOfBirth} onChange={set('dateOfBirth')} />, 'Date of birth')}
          <Select
            label="Gender"
            value={v.gender}
            onChange={set('gender')}
            placeholder="Not given"
            options={[
              { value: 'M', label: 'Male' },
              { value: 'F', label: 'Female' },
              { value: 'X', label: 'Other' },
            ]}
          />
          <Field label="Mobile" value={v.contactNo} onChange={set('contactNo')} placeholder="0300-1234567" />
          <Field label="Other contact" value={v.otherContact} onChange={set('otherContact')} />
          {masked('homeAddress', <TextArea label="Home address" value={v.homeAddress} onChange={set('homeAddress')} rows={2} />, 'Home address')}
          {masked('emergencyName', <Field label="In an emergency, call" value={v.emergencyName} onChange={set('emergencyName')} />, 'In an emergency, call')}
          {masked('emergencyPhone', <Field label="Their phone" value={v.emergencyPhone} onChange={set('emergencyPhone')} />, 'Their phone')}
        </FormGroup>

        <FormGroup title="Job" columns={3} card>
          {editing && (
            <Field
              label="Code"
              mono
              value={editing.code}
              onChange={() => {}}
              disabled
              hint="Given when it was created, and never changes"
            />
          )}
          {creating ? (
            <Select
              label="Worker type"
              required
              value={workerType}
              onChange={(x) => {
                setWorkerType(x as '' | 'MONTHLY' | 'DAILY');
                if (x === 'MONTHLY') set('payFrequency')('MONTHLY');
              }}
              options={[
                { value: 'MONTHLY', label: WORKER_TYPE_TEXT.MONTHLY },
                { value: 'DAILY', label: WORKER_TYPE_TEXT.DAILY },
              ]}
            />
          ) : (
            <Field label="Worker type" value={WORKER_TYPE_TEXT[editing.workerType]} onChange={() => {}} disabled />
          )}
          <Select label="Department" value={v.departmentId} onChange={set('departmentId')} placeholder="None" options={named(lookups.data?.departments)} />
          <Select label="Designation" value={v.designationId} onChange={set('designationId')} placeholder="None" options={named(lookups.data?.designations)} />
          {creating ? (
            <>
              <Select
                label="Farm"
                required={!allFarms}
                value={farmId}
                onChange={(x) => {
                  setFarmId(x);
                  setShedId('');
                }}
                placeholder={allFarms ? 'None — head office' : undefined}
                options={farmOptions}
              />
              <Select
                label="Shed"
                value={shedId}
                onChange={setShedId}
                disabled={!farmId}
                placeholder={farmId ? 'Not tied to one shed' : undefined}
                prompt="Choose a farm first"
                options={(sheds.data ?? []).map((s) => ({ value: s.id, label: `${s.code} · ${s.nameEn}` }))}
              />
            </>
          ) : (
            <Field
              label="Farm"
              value={`${farmLabel}${editing.shedName ? ` · ${editing.shedName}` : ''}`}
              onChange={() => {}}
              disabled
              hint="Use Move to another farm to change it"
            />
          )}
          <Select
            label="Reports to"
            value={v.reportsToId}
            onChange={set('reportsToId')}
            placeholder="Nobody"
            options={(people.data?.items ?? [])
              .filter((p) => p.id !== editing?.id)
              .map((p) => ({ value: p.id, label: employeeLabel(p) }))}
          />
          <Select
            label="Shift"
            value={v.shiftId}
            onChange={set('shiftId')}
            placeholder="No set shift"
            options={(lookups.data?.shifts ?? []).map((s) => ({ value: s.id, label: `${s.nameEn} (${s.startTime}–${s.endTime})` }))}
          />
          {creating ? (
            <Field label="Joining date" type="date" required value={joinedOn} onChange={setJoinedOn} />
          ) : (
            <Field label="Joining date" type="date" value={editing.joinedOn} onChange={() => {}} disabled />
          )}
          <Field label="Probation ends" type="date" value={v.probationEnd} onChange={set('probationEnd')} />
          <Select
            label="Paid"
            value={workerType === 'MONTHLY' ? 'MONTHLY' : v.payFrequency}
            onChange={set('payFrequency')}
            disabled={workerType !== 'DAILY'}
            placeholder=""
            options={Object.entries(FREQUENCY_TEXT).map(([value, label]) => ({ value, label }))}
            hint={workerType === 'DAILY' ? 'Daily-wage staff can be paid weekly, fortnightly or monthly.' : 'Monthly-salary staff are paid monthly.'}
          />
          <div className="field">
            <SearchSelect
              label="Sign-in account"
              value={v.userId}
              selectedLabel={editing?.user ? `${editing.user.fullName} (${editing.user.userId})` : undefined}
              search={async (query): Promise<SearchOption[]> => {
                const rows = (await api.get('/api/v1/hr/employees/sign-in-accounts', {
                  search: query,
                  employeeId: editing?.id,
                })) as Array<{ id: string; userId: string; fullName: string }>;
                return rows.map((r) => ({ value: r.id, label: `${r.fullName} (${r.userId})` }));
              }}
              placeholder="Search by name or user ID…"
              hint="Optional. Lets them see their own payslips in My Pay. Clear it to unlink."
              onChange={(o) => set('userId')(o?.value ?? '')}
            />
          </div>
          <div className="formgrid__full">
            <span className="field__label">Weekly off days</span>
            <div className="employees__days">
              {WEEKDAYS.map((day, i) => (
                <Check
                  key={day}
                  label={day}
                  checked={v.weeklyOff.includes(i)}
                  onChange={(on) =>
                    setV((s) => ({
                      ...s,
                      weeklyOff: on ? [...s.weeklyOff, i].sort() : s.weeklyOff.filter((d) => d !== i),
                    }))
                  }
                />
              ))}
            </div>
          </div>
          <Check label="Gets overtime" checked={v.overtimeEligible} onChange={flag('overtimeEligible')} />
        </FormGroup>

        {creating && (
          <FormGroup
            title="Pay"
            columns={3}
            card
            hint={
              workerType === 'MONTHLY'
                ? 'Leave an item empty if it is not paid. Later changes go through Change pay.'
                : workerType === 'DAILY'
                  ? 'Paid for each day worked. Later changes go through Change pay.'
                  : 'Choose the worker type first.'
            }
          >
            {workerType === 'MONTHLY' && <PayLinesEditor components={components} amounts={amounts} onChange={setAmounts} />}
            {workerType === 'DAILY' && <Field label="Daily rate" type="number" min={0} required value={dailyRate} onChange={setDailyRate} />}
          </FormGroup>
        )}

        <FormGroup title="Payment" columns={3} card>
          <Select
            label="Paid by"
            value={v.paymentMethod}
            onChange={set('paymentMethod')}
            placeholder=""
            options={Object.entries(PAY_METHOD_TEXT).map(([value, label]) => ({ value, label }))}
          />
          {v.paymentMethod === 'BANK' && (
            <>
              {masked('payBankName', <Field label="Bank" value={v.payBankName} onChange={set('payBankName')} placeholder="e.g. HBL" />, 'Bank')}
              {masked('payAccountTitle', <Field label="Account title" value={v.payAccountTitle} onChange={set('payAccountTitle')} />, 'Account title')}
              {masked(
                'payIban',
                <Field
                  label="IBAN"
                  mono
                  span={2}
                  value={v.payIban}
                  onChange={(x) => set('payIban')(cleanIban(x))}
                  placeholder="PK36SCBL0000001123456702"
                  hint="24 characters, starting PK"
                />,
                'IBAN',
              )}
            </>
          )}
          {v.paymentMethod === 'WALLET' && (
            <>
              <Select
                label="Wallet"
                required
                value={v.walletProvider}
                onChange={set('walletProvider')}
                options={[
                  { value: 'JAZZCASH', label: 'JazzCash' },
                  { value: 'EASYPAISA', label: 'Easypaisa' },
                ]}
              />
              {masked('walletNo', <Field label="Wallet number" mono value={v.walletNo} onChange={set('walletNo')} placeholder="03001234567" />, 'Wallet number')}
            </>
          )}
        </FormGroup>

        <FormGroup title="Government" columns={3} card hint="EOBI, PESSI and income tax only apply once they are switched on in HR Setup.">
          <Check label="Registered with EOBI" checked={v.eobiRegistered} onChange={flag('eobiRegistered')} />
          {v.eobiRegistered ? masked('eobiNo', <Field label="EOBI number" value={v.eobiNo} onChange={set('eobiNo')} />, 'EOBI number') : <span />}
          <span />
          <Check label="Registered with PESSI" checked={v.pessiRegistered} onChange={flag('pessiRegistered')} />
          {v.pessiRegistered ? masked('pessiNo', <Field label="PESSI number" value={v.pessiNo} onChange={set('pessiNo')} />, 'PESSI number') : <span />}
          <span />
          <Check label="Income tax is taken from pay" checked={v.taxApplies} onChange={flag('taxApplies')} />
          <Field label="NTN" value={v.ntn} onChange={set('ntn')} hint="Optional" />
          <span />
          {masked(
            'openingTaxableYtd',
            <Field
              label="Taxable pay so far this tax year"
              type="number"
              min={0}
              value={v.openingTaxableYtd}
              onChange={set('openingTaxableYtd')}
              hint="Only if they start part-way through the year"
            />,
            'Taxable pay so far this tax year',
          )}
          {masked(
            'openingTaxYtd',
            <Field label="Tax already taken this tax year" type="number" min={0} value={v.openingTaxYtd} onChange={set('openingTaxYtd')} />,
            'Tax already taken this tax year',
          )}
          <Select
            label="Tax year"
            value={v.openingTaxYear}
            onChange={set('openingTaxYear')}
            placeholder=""
            options={[taxYear, taxYear - 1].map((y) => ({ value: String(y), label: `July ${y} – June ${y + 1} (${taxYearLabel(y)})` }))}
          />
        </FormGroup>

        {lookups.error && <ErrorNote message={lookups.error.message} />}
        {problem && <ErrorNote message={problem} />}
        <FormActions
          onCancel={() => {
            draft.forget();
            onCancel();
          }}
        >
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add person'}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}
